import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AcademicCapIcon,
  BadgeCheckIcon,
  BookmarkIcon,
  CheckCircleIcon,
  ClipboardListIcon,
  FilterIcon,
  GlobeAltIcon,
  HeartIcon,
  LocationMarkerIcon,
  OfficeBuildingIcon,
  PaperAirplaneIcon,
  SearchIcon,
  ShieldCheckIcon,
  SparklesIcon,
} from "@heroicons/react/solid";
import { BACKEND_URL } from "../config";

const processingCountryExamples = [
  "United Kingdom",
  "United States",
  "Canada",
  "Australia",
  "Ireland",
  "Germany",
  "New Zealand",
  "France",
];

const workSteps = [
  {
    title: "Search & Explore",
    icon: SearchIcon,
    text: "Students search consultancies by city, state, processing country, consultancy name, and services.",
  },
  {
    title: "Compare",
    icon: FilterIcon,
    text: "Students compare processing countries, services, locations, consultancy profiles, available offers, and verification status.",
  },
  {
    title: "Shortlist & Connect",
    icon: BookmarkIcon,
    text: "Students can save preferred consultancies and contact the consultancy that matches their requirements.",
  },
  {
    title: "Start the Study-Abroad Journey",
    icon: AcademicCapIcon,
    text: "Students can speak directly with their selected consultancy and continue their admission process.",
  },
];

const studentBenefits = [
  ["Discover consultancies", SearchIcon],
  ["Search by processing country", GlobeAltIcon],
  ["Search by city or state", LocationMarkerIcon],
  ["Compare services", ClipboardListIcon],
  ["Compare consultancy profiles", FilterIcon],
  ["View offers", SparklesIcon],
  ["Save favourites", HeartIcon],
  ["Contact consultancies", PaperAirplaneIcon],
];

const consultancyBenefits = [
  "Get listed",
  "Claim their consultancy profile",
  "Add processing countries",
  "Add services",
  "Add branches",
  "Publish offers",
  "Receive student enquiries",
  "Improve their visibility",
];

const reasons = [
  {
    title: "Discover More Options",
    text: "Students can explore multiple consultancies instead of depending only on advertisements or referrals.",
  },
  {
    title: "Compare Before Deciding",
    text: "Students can understand processing countries and services before contacting a consultancy.",
  },
  {
    title: "Transparent Information",
    text: "Clearly show consultancy information and verification status.",
  },
  {
    title: "Direct Connection",
    text: "Students can connect with consultancies that match their requirements.",
  },
  {
    title: "Student-Focused Discovery",
    text: "Make consultancy discovery easier and organised.",
  },
];

function StatCard({ value, label }) {
  if (!value) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <p className="text-2xl font-semibold text-slate-950">{value.toLocaleString()}</p>
      <p className="mt-1 text-sm text-slate-500">{label}</p>
    </div>
  );
}

function InfoCard({ icon: Icon, title, children }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
      <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-cyan-50 text-cyan-700">
        <Icon className="h-6 w-6" />
      </div>
      <h3 className="mt-5 text-lg font-semibold text-slate-950">{title}</h3>
      <div className="mt-3 text-sm leading-6 text-slate-600">{children}</div>
    </div>
  );
}

export default function About() {
  const [stats, setStats] = useState({ consultancies: 0, cities: 0, countries: [] });
  const [founderImageFailed, setFounderImageFailed] = useState(false);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const [listRes, facetsRes] = await Promise.all([
          fetch(`${BACKEND_URL}/consultancies?limit=1`),
          fetch(`${BACKEND_URL}/consultancies/facets`),
        ]);
        const listData = listRes.ok ? await listRes.json() : {};
        const facets = facetsRes.ok ? await facetsRes.json() : {};
        setStats({
          consultancies: Number(listData.totalCount || 0),
          cities: Array.isArray(facets.cities) ? facets.cities.length : 0,
          countries: Array.isArray(facets.countries) ? facets.countries : [],
        });
      } catch (err) {
        console.error("About page stats error", err);
      }
    };
    loadStats();
  }, []);

  const visibleCountries = useMemo(() => {
    const stored = new Set(stats.countries);
    const examples = processingCountryExamples.filter((country) => stored.has(country));
    return examples.length ? examples : stats.countries.slice(0, 8);
  }, [stats.countries]);

  const scrollToHowItWorks = () => {
    document.getElementById("how-abroadjacks-works")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="bg-slate-50 text-slate-950">
      <section className="bg-slate-950 text-white">
        <div className="mx-auto grid max-w-7xl gap-8 px-4 py-10 sm:px-6 sm:py-14 lg:grid-cols-[1fr_420px] lg:items-center lg:py-20">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[3px] text-cyan-300 sm:tracking-[4px]">About AbroadJacks</p>
            <h1 className="mt-4 max-w-4xl text-[clamp(2rem,7.2vw,4rem)] font-semibold leading-[1.08] tracking-tight">
              <span className="block sm:inline">Empowering</span>{" "}
              <span className="block sm:inline">Students.</span>
              <span className="block sm:inline">Connecting</span>{" "}
              <span className="block sm:inline">Opportunities.</span>
            </h1>
            <p className="mt-6 max-w-3xl text-base leading-7 text-slate-300 sm:text-lg">
              AbroadJacks is a study-abroad discovery platform that helps students find, explore and compare overseas education consultancies based on location, processing countries, services and available offers.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link to="/consultancies" className="inline-flex items-center justify-center rounded-lg bg-cyan-300 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-cyan-200">
                Explore Consultancies
              </Link>
              <button type="button" onClick={scrollToHowItWorks} className="inline-flex items-center justify-center rounded-lg border border-white/20 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/10">
                How It Works
              </button>
            </div>
          </div>

          <div className="grid gap-3 min-[420px]:grid-cols-3 lg:grid-cols-1">
            <StatCard value={stats.consultancies} label="Consultancies listed" />
            <StatCard value={stats.cities} label="Cities available" />
            <StatCard value={stats.countries.length} label="Processing countries in listings" />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14">
        <div className="grid gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[3px] text-cyan-700">Platform</p>
            <h2 className="mt-2 text-2xl font-semibold text-slate-950 sm:text-3xl">What is AbroadJacks?</h2>
          </div>
          <div className="space-y-5 rounded-lg border border-slate-200 bg-white p-5 leading-7 text-slate-600 shadow-sm sm:p-6">
            <p>AbroadJacks is designed to make the study-abroad discovery process simpler and more transparent.</p>
            <p>Instead of searching across many websites, social-media pages and advertisements, students can use AbroadJacks to discover overseas education consultancies in one place.</p>
            <p>Students can explore consultancy profiles, see which countries they process, understand their services, view available offers, shortlist consultancies and connect with the ones that match their requirements.</p>
            <p className="font-medium text-slate-800">AbroadJacks is a discovery and comparison platform. It is not a university, embassy, visa authority or government organisation.</p>
          </div>
        </div>
      </section>

      <section id="how-abroadjacks-works" className="border-y border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[3px] text-cyan-700">Process</p>
            <h2 className="mt-2 text-2xl font-semibold text-slate-950 sm:text-3xl">How AbroadJacks Works</h2>
          </div>
          <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {workSteps.map((step, index) => (
              <InfoCard key={step.title} icon={step.icon} title={`Step ${index + 1} - ${step.title}`}>
                {step.text}
              </InfoCard>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14">
        <div className="grid gap-8 lg:grid-cols-[1fr_1fr] lg:items-center">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[3px] text-cyan-700">Processing Countries</p>
            <h2 className="mt-2 text-2xl font-semibold text-slate-950 sm:text-3xl">Search by destination and location</h2>
            <p className="mt-4 leading-7 text-slate-600">
              AbroadJacks allows students to search consultancies based on the countries they process. A student should eventually be able to search United Kingdom + Hyderabad and see consultancies in Hyderabad that process UK applications.
            </p>
            <p className="mt-4 text-sm text-slate-500">
              Processing countries shown here come from stored consultancy data. Countries are not invented for any consultancy.
            </p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="flex flex-wrap gap-3">
              {(visibleCountries.length ? visibleCountries : ["Countries appear when stored in listings"]).map((country) => (
                <span key={country} className="rounded-full border border-cyan-200 bg-cyan-50 px-4 py-2 text-sm font-semibold text-cyan-800">
                  {country}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white">
        <div className="mx-auto grid max-w-7xl gap-8 px-4 py-10 sm:px-6 sm:py-14 lg:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[3px] text-cyan-700">For Students</p>
            <h2 className="mt-2 text-2xl font-semibold text-slate-950 sm:text-3xl">Explore multiple options before deciding</h2>
            <div className="mt-7 grid gap-3 sm:grid-cols-2">
              {studentBenefits.map(([benefit, Icon]) => (
                <div key={benefit} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <Icon className="h-5 w-5 flex-none text-cyan-700" />
                  <span className="text-sm font-medium text-slate-700">{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-950 p-5 text-white shadow-sm sm:p-6">
            <p className="text-xs font-semibold uppercase tracking-[3px] text-cyan-300">For Education Consultancies</p>
            <h2 className="mt-2 text-2xl font-semibold sm:text-3xl">Build a clearer public profile</h2>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {consultancyBenefits.map((benefit) => (
                <div key={benefit} className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.06] p-4">
                  <CheckCircleIcon className="h-5 w-5 flex-none text-cyan-300" />
                  <span className="text-sm text-slate-200">{benefit}</span>
                </div>
              ))}
            </div>
            <Link to="/consultancies" className="mt-7 inline-flex rounded-lg bg-cyan-300 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-cyan-200">
              Claim Your Consultancy
            </Link>
            <p className="mt-3 text-sm text-slate-400">
              Open your consultancy profile and use the claim option for admin review.
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14">
        <div className="grid gap-8 lg:grid-cols-[420px_1fr] lg:items-center">
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            {!founderImageFailed ? (
              <img
                src="/akhil-varma-founder.jpg"
                alt="Akhil Varma, Founder of AbroadJacks"
                className="aspect-[4/5] w-full object-cover"
                onError={() => setFounderImageFailed(true)}
              />
            ) : (
              <div className="flex aspect-[4/5] w-full items-center justify-center bg-slate-950 text-white">
                <div className="text-center">
                  <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full border border-white/20 bg-white/10 text-3xl font-semibold">
                    AV
                  </div>
                  <p className="mt-4 text-sm text-slate-300">Founder photo pending</p>
                </div>
              </div>
            )}
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-[3px] text-cyan-700">Meet the Founder</p>
            <h2 className="mt-2 text-2xl font-semibold text-slate-950 sm:text-3xl">Akhil Varma</h2>
            <p className="mt-1 text-lg text-slate-600">Founder, AbroadJacks</p>

            <div className="mt-6 grid gap-3 sm:grid-cols-3">
              <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-sm font-semibold text-slate-950">🎓 Master's in Cyber Security</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-sm font-semibold text-slate-950">🏫 Charles Sturt University</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-sm font-semibold text-slate-950">🇦🇺 Australia</p>
              </div>
            </div>

            <div className="mt-7 space-y-5 leading-7 text-slate-600">
              <p className="font-medium text-slate-800">
                Akhil Varma is the Founder of AbroadJacks and an international graduate of Charles Sturt University, Australia, where he completed his Master's in Cyber Security.
              </p>
              <p>Having personally experienced the international education journey, he understands how much research students may need to do before selecting a study destination, university and overseas education consultancy.</p>
              <p>AbroadJacks was created with the idea of using technology to organise the study-abroad consultancy ecosystem and make it easier for students to discover and compare the options available to them.</p>
              <p>His vision is to build AbroadJacks into a useful platform where students can explore overseas education opportunities with greater clarity while giving genuine study-abroad consultancies a platform to connect with students.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white">
        <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 sm:py-14">
          <div className="rounded-lg border border-cyan-100 bg-cyan-50 p-5 shadow-sm sm:p-8">
            <p className="text-xs font-semibold uppercase tracking-[3px] text-cyan-800">A Message from the Founder</p>
            <blockquote className="mt-5 space-y-5 text-base leading-7 text-slate-800 sm:text-lg sm:leading-8">
              <p>Having studied internationally myself, I understand how many decisions students have to make before beginning their study-abroad journey.</p>
              <p>Finding the right information, understanding different options and choosing the right support can be challenging.</p>
              <p>AbroadJacks was created with a simple idea - bring study-abroad consultancies, services and opportunities together in one place.</p>
              <p>Our aim is to use technology to make overseas education discovery simpler, clearer and more accessible for students.</p>
            </blockquote>
            <div className="mt-6">
              <p className="font-semibold text-slate-950">Akhil Varma</p>
              <p className="text-sm text-slate-600">Founder, AbroadJacks</p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14">
        <div className="grid gap-5 lg:grid-cols-2">
          <InfoCard icon={ShieldCheckIcon} title="Our Mission">
            To simplify study-abroad discovery by helping students find, understand and compare overseas education consultancies and opportunities through one easy-to-use digital platform.
          </InfoCard>
          <InfoCard icon={OfficeBuildingIcon} title="Our Vision">
            To build a comprehensive study-abroad discovery ecosystem that connects students with overseas education consultancies and opportunities across destinations around the world.
          </InfoCard>
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[3px] text-cyan-700">Why AbroadJacks?</p>
            <h2 className="mt-2 text-2xl font-semibold text-slate-950 sm:text-3xl">Built for organised study-abroad discovery</h2>
          </div>
          <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-5">
            {reasons.map((reason) => (
              <InfoCard key={reason.title} icon={BadgeCheckIcon} title={reason.title}>
                {reason.text}
              </InfoCard>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-950 text-white">
        <div className="mx-auto max-w-7xl px-4 py-10 text-center sm:px-6 sm:py-14">
          <h2 className="text-2xl font-semibold sm:text-3xl">Ready to Explore Your Study-Abroad Options?</h2>
          <p className="mx-auto mt-4 max-w-2xl text-slate-300">
            Discover overseas education consultancies based on your preferred destination, location and requirements.
          </p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Link to="/consultancies" className="inline-flex justify-center rounded-lg bg-cyan-300 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-cyan-200">
              Explore Consultancies
            </Link>
            <Link to="/destinations" className="inline-flex justify-center rounded-lg border border-white/20 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/10">
              Explore Countries
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
